import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  Badge,
  Card,
  CardBody,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  VStack,
  Box,
  Divider,
  Select,
  Checkbox,
  Grid,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  HStack,
  Icon,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import 'moment/locale/pt-br';
import { createSearchParams, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { FaFileInvoice } from 'react-icons/fa6';
import { CaretDown, CaretUp, XCircle, MagnifyingGlass } from 'phosphor-react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Download,
  FileText,
  FileArchive,
  ExternalLink,
} from 'lucide-react';
import { ReturnButton } from '../../../../../components/buttons/ReturnButton';
import { PageBox } from '../../../../../components/pageBox';
import { FormBox } from '../../../../../components/formBox';
import { Paginator } from '../../../../../components/paginator';
import { IData, IDataNoPagination, IPagination } from '../../../../../types/shared';
import {
  formatCNPJ,
  formatCPF,
  formatCurrency,
  normalizeMonetaryValue,
} from '../../../../../utils/FormatFunctions';
import {
  getIssApurado,
  getIssProprio,
  getIssRetido,
  getRetPisCofinsLabel,
  isIssRetido,
} from '../../../../../utils/nfseCampos';
import { INfse, INfseStats, useNfse } from '../../../../../hooks/useNfse';
import { generateNfsePdf, exportNfsesToZip } from '../../../../../utils/nfsePdfGenerator';
import { exportNfseTablePdf } from '../../../../../utils/nfseTablePdfExport';
import { ExportPdfModal, ExportMode } from './ExportPdfModal';

moment.locale('pt-br');

// eslint-disable-next-line no-unused-vars
type SelectMonthHandler = (year: number, month: number) => void;

interface MonthNavigatorProps {
  currentDate: moment.Moment;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectMonth: SelectMonthHandler;
}

function MonthNavigator({
  currentDate,
  onPrevMonth,
  onNextMonth,
  onSelectMonth,
}: MonthNavigatorProps) {
  const monthYear = currentDate.format('MMMM [de] YYYY');
  const isCurrentMonth = currentDate.isSame(moment(), 'month');
  const [selectedYear, setSelectedYear] = useState(currentDate.year());

  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];

  const currentYear = moment().year();
  const currentMonth = moment().month();

  const handleYearChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setSelectedYear(prev => prev - 1);
    } else if (direction === 'next' && selectedYear < currentYear) {
      setSelectedYear(prev => prev + 1);
    }
  };

  const isMonthDisabled = (monthIndex: number) => {
    return selectedYear === currentYear && monthIndex > currentMonth;
  };

  const isMonthSelected = (monthIndex: number) => {
    return selectedYear === currentDate.year() && monthIndex === currentDate.month();
  };

  return (
    <HStack spacing={2} bg="white" px={4} py={2} borderRadius="lg" shadow="sm">
      <IconButton
        aria-label="Mês anterior"
        icon={<Icon as={ChevronLeft} />}
        onClick={onPrevMonth}
        variant="ghost"
        size="sm"
        colorScheme="blue"
      />
      <Popover placement="bottom">
        <PopoverTrigger>
          <HStack
            spacing={2}
            minW="180px"
            justify="center"
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
            px={2}
            py={1}
            borderRadius="md"
          >
            <Icon as={Calendar} color="gray.500" boxSize={4} />
            <Text fontWeight="medium" fontSize="sm" textTransform="capitalize" color="gray.700">
              {monthYear}
            </Text>
          </HStack>
        </PopoverTrigger>
        <PopoverContent width="280px">
          <PopoverBody p={4}>
            {/* Seletor de Ano */}
            <HStack justify="space-between" mb={4}>
              <IconButton
                aria-label="Ano anterior"
                icon={<Icon as={ChevronLeft} boxSize={4} />}
                onClick={() => handleYearChange('prev')}
                variant="ghost"
                size="sm"
              />
              <Text fontWeight="bold" fontSize="md">
                {selectedYear}
              </Text>
              <IconButton
                aria-label="Próximo ano"
                icon={<Icon as={ChevronRight} boxSize={4} />}
                onClick={() => handleYearChange('next')}
                variant="ghost"
                size="sm"
                isDisabled={selectedYear >= currentYear}
              />
            </HStack>
            {/* Grid de Meses */}
            <Grid templateColumns="repeat(4, 1fr)" gap={2}>
              {months.map((month, index) => (
                <Button
                  key={month}
                  size="sm"
                  variant={isMonthSelected(index) ? 'solid' : 'ghost'}
                  colorScheme={isMonthSelected(index) ? 'blue' : 'gray'}
                  isDisabled={isMonthDisabled(index)}
                  onClick={() => {
                    onSelectMonth(selectedYear, index);
                  }}
                  fontSize="xs"
                >
                  {month}
                </Button>
              ))}
            </Grid>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <IconButton
        aria-label="Próximo mês"
        icon={<Icon as={ChevronRight} />}
        onClick={onNextMonth}
        variant="ghost"
        size="sm"
        colorScheme="blue"
        isDisabled={isCurrentMonth}
      />
    </HStack>
  );
}

export function ModulesHomeIntegrationNfseManagementShowNotasPage() {
  const { itemId: id } = useParams();
  const location = useLocation();
  const { getNfseList, getAllNfse, getNfseStats, getMunicipiosRetencaoNfse } = useNfse();
  const toast = useToast();
  const company = location.state?.company;

  const queryParams = new URLSearchParams(location.search);
  const searchParamStart = queryParams.get('date_start');
  const searchParamEnd = queryParams.get('date_end');

  const [filters, setFilters] = useSearchParams({
    page: '1',
    size: '50',
    codi_emp: id || '',
    date_start: searchParamStart || moment().startOf('month').format('YYYY-MM-DD'),
    date_end: searchParamEnd || moment().endOf('month').format('YYYY-MM-DD'),
  });

  // Estado para ordenação
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // Refs para sincronizar barras de rolagem
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(1800);

  const handleTopScroll = useCallback(() => {
    if (topScrollRef.current && tableContainerRef.current) {
      tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  }, []);

  const handleTableScroll = useCallback(() => {
    if (topScrollRef.current && tableContainerRef.current) {
      topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  }, []);

  // Filtros de impostos individuais
  const [filtrosImpostos, setFiltrosImpostos] = useState<{
    pis: 'todos' | 'com' | 'sem';
    cofins: 'todos' | 'com' | 'sem';
    irpj: 'todos' | 'com' | 'sem';
    csll: 'todos' | 'com' | 'sem';
    ir: 'todos' | 'com' | 'sem';
    irrf: 'todos' | 'com' | 'sem';
    issqn: 'todos' | 'com' | 'sem';
    inss: 'todos' | 'com' | 'sem';
    iss: 'todos' | 'com' | 'sem';
  }>({
    pis: 'todos',
    cofins: 'todos',
    irpj: 'todos',
    csll: 'todos',
    ir: 'todos',
    irrf: 'todos',
    issqn: 'todos',
    inss: 'todos',
    iss: 'todos',
  });

  // Filtros adicionais
  const [filtroTipo, setFiltroTipo] = useState<'' | 'prestado' | 'tomado'>('');
  const [filtroCnpj, setFiltroCnpj] = useState<string>('');
  const [filtroMunicipioRetencao, setFiltroMunicipioRetencao] = useState<string>('todos');
  const [filtroRetencaoFederal, setFiltroRetencaoFederal] = useState<'' | 'tomador' | 'prestador'>(
    '',
  );
  const [filtroRetencaoMunicipal, setFiltroRetencaoMunicipal] = useState<
    '' | 'tomador' | 'prestador'
  >('');
  const [filtroCancelamento, setFiltroCancelamento] = useState<
    'todos' | 'canceladas' | 'substituidas' | 'ativas'
  >('todos');

  // Filtro por tipo de retenção (card de estatísticas)
  const [filtroTipoRetencao, setFiltroTipoRetencao] = useState<
    '' | 'federal' | 'pis_cofins' | 'municipal' | 'sem_retencao'
  >('');

  // Estado para filtros de coluna (enviados ao backend)
  const [filtrosColuna, setFiltrosColuna] = useState<Record<string, string>>({});

  // Estado para exportação em lote
  const [isExportingZip, setIsExportingZip] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [isExportPdfModalOpen, setIsExportPdfModalOpen] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Função para atualizar filtro de coluna
  const handleFiltroColuna = (coluna: string, valor: string) => {
    setFiltrosColuna(prev => {
      if (valor === '' || valor === 'todos') {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
        const { [coluna]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [coluna]: valor };
    });
  };

  // Parâmetros completos para o backend
  const filtrosCompletos = useMemo(() => {
    const params: any = {
      codi_emp: id,
      date_start: filters.get('date_start'),
      date_end: filters.get('date_end'),
      page: filters.get('page'),
      size: filters.get('size'),
    };

    // Filtro por tipo (prestado/tomado)
    if (filtroTipo !== '') {
      params.tipo = filtroTipo;
    }

    // Filtro por CNPJ
    if (filtroCnpj.trim() !== '') {
      params.cnpj = filtroCnpj.trim();
    }

    // Filtro por município de retenção
    if (filtroMunicipioRetencao !== 'todos') {
      params.municipio_retencao = filtroMunicipioRetencao;
    }

    // Filtro por retenção federal
    if (filtroRetencaoFederal !== '') {
      params.retencao_federal = filtroRetencaoFederal;
    }

    // Filtro por retenção municipal
    if (filtroRetencaoMunicipal !== '') {
      params.retencao_municipal = filtroRetencaoMunicipal;
    }

    // Filtro de cancelamento
    if (filtroCancelamento !== 'todos') {
      params.cancelamento = filtroCancelamento;
    }

    // Filtros de impostos individuais
    Object.entries(filtrosImpostos).forEach(([imposto, filtro]) => {
      if (filtro !== 'todos') {
        params[`${imposto}_filtro`] = filtro;
      }
    });

    // Filtro por tipo de retenção (do card de estatísticas)
    if (filtroTipoRetencao === 'federal') {
      params.retencao_federal = 'tomador';
    } else if (filtroTipoRetencao === 'pis_cofins') {
      params.filtro_com_retencao_pis_cofins = '1';
    } else if (filtroTipoRetencao === 'municipal') {
      params.retencao_municipal = 'tomador';
    } else if (filtroTipoRetencao === 'sem_retencao') {
      // Espelho exato do total_sem_retencao do card (sem federal, sem
      // municipal e sem código de retenção PIS/COFINS)
      params.filtro_sem_retencao = '1';
    }

    // Parâmetros de ordenação
    if (orderBy) {
      params.order_by = orderBy;
      params.order = order;
    }

    // Filtros de texto por coluna
    // Mapeamento de colunas do frontend para parâmetros do backend
    const columnToParam: Record<string, string> = {
      numero_nfse: 'filtro_numero_nfse',
      chave_nfse: 'filtro_chave_nfse',
      emissor_nome: 'filtro_emissor_nome',
      emissor_cnpj: 'filtro_emissor_cnpj',
      emissor_im: 'filtro_emissor_im',
      local_emissao: 'filtro_local_emissao',
      tomador_nome: 'filtro_tomador_nome',
      tomador_cnpj: 'filtro_tomador_cnpj',
      tomador_municipio: 'filtro_tomador_municipio',
      servico_codigo: 'filtro_servico_codigo',
      tributo_nacional: 'filtro_tributo_nacional',
      servico_descricao: 'filtro_servico_descricao',
      discriminacao: 'filtro_servico_descricao', // discriminacao usa servico_descricao
      servico_codigo_trib_mun: 'filtro_servico_codigo_trib_mun',
      local_incidencia: 'filtro_local_incidencia',
      municipio_incidencia: 'filtro_local_incidencia', // coluna texto usa o mesmo campo
      tp_ret_pis_cofins: 'filtro_tp_ret_pis_cofins',
    };

    Object.entries(filtrosColuna).forEach(([coluna, valor]) => {
      if (valor && valor.trim() !== '' && valor !== 'todos') {
        const paramName = columnToParam[coluna];
        if (paramName) {
          params[paramName] = valor.trim();
        }
      }
    });

    return params;
  }, [
    id,
    filters,
    filtroTipo,
    filtroCnpj,
    filtroMunicipioRetencao,
    filtroRetencaoFederal,
    filtroRetencaoMunicipal,
    filtroCancelamento,
    filtroTipoRetencao,
    filtrosImpostos,
    orderBy,
    order,
    filtrosColuna,
  ]);

  // Stats usa os MESMOS filtros da tabela (senão os cards não acompanham o
  // que o usuário está vendo), só sem paginação/ordenação — o resultado não
  // depende delas e mantê-las no queryKey refazia os cards a cada página.
  const statsParams = useMemo(() => {
    const params: Record<string, any> = { ...filtrosCompletos };
    delete params.page;
    delete params.size;
    delete params.order_by;
    delete params.order;
    return params;
  }, [filtrosCompletos]);

  const handleToggleFiltroImposto = (
    imposto: keyof typeof filtrosImpostos,
    tipo: 'com' | 'sem',
  ) => {
    setFiltrosImpostos(prev => ({
      ...prev,
      [imposto]: prev[imposto] === tipo ? 'todos' : tipo,
    }));
  };

  const handleLimparFiltrosImpostos = () => {
    setFiltrosImpostos({
      pis: 'todos',
      cofins: 'todos',
      irpj: 'todos',
      csll: 'todos',
      ir: 'todos',
      irrf: 'todos',
      issqn: 'todos',
      inss: 'todos',
      iss: 'todos',
    });
    setFiltroTipo('');
    setFiltroCnpj('');
    setFiltroMunicipioRetencao('todos');
    setFiltroRetencaoFederal('');
    setFiltroRetencaoMunicipal('');
    setFiltroCancelamento('todos');
    setFiltroTipoRetencao('');
  };

  const contadorFiltrosAtivos = useMemo(() => {
    const filtrosImpostosAtivos = Object.values(filtrosImpostos).filter(v => v !== 'todos').length;
    const filtroTipoAtivo = filtroTipo !== '' ? 1 : 0;
    const filtroCnpjAtivo = filtroCnpj.trim() !== '' ? 1 : 0;
    const filtroMunicipioAtivo = filtroMunicipioRetencao !== 'todos' ? 1 : 0;
    const filtroRetencaoFederalAtivo = filtroRetencaoFederal !== '' ? 1 : 0;
    const filtroRetencaoMunicipalAtivo = filtroRetencaoMunicipal !== '' ? 1 : 0;
    const filtroCancelamentoAtivo = filtroCancelamento !== 'todos' ? 1 : 0;
    const filtroTipoRetencaoAtivo = filtroTipoRetencao !== '' ? 1 : 0;
    return (
      filtrosImpostosAtivos +
      filtroTipoAtivo +
      filtroCnpjAtivo +
      filtroMunicipioAtivo +
      filtroRetencaoFederalAtivo +
      filtroRetencaoMunicipalAtivo +
      filtroCancelamentoAtivo +
      filtroTipoRetencaoAtivo
    );
  }, [
    filtrosImpostos,
    filtroTipo,
    filtroCnpj,
    filtroMunicipioRetencao,
    filtroRetencaoFederal,
    filtroRetencaoMunicipal,
    filtroCancelamento,
    filtroTipoRetencao,
  ]);

  // Reseta para página 1 quando filtros locais mudam
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Ignora a primeira renderização para não resetar a página ao carregar
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const currentPage = filters.get('page');
    if (currentPage !== '1') {
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          page: '1',
        }),
      );
    }
  }, [
    filtroTipo,
    filtroCnpj,
    filtroMunicipioRetencao,
    filtroRetencaoFederal,
    filtroRetencaoMunicipal,
    filtroCancelamento,
    filtroTipoRetencao,
    filtrosImpostos,
    filtrosColuna,
  ]);

  // Função para ordenar coluna
  const handleSort = (column: string) => {
    if (orderBy === column) {
      // Se já está ordenando por esta coluna, inverte a direção
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // Se é uma nova coluna, ordena em ordem ascendente
      setOrderBy(column);
      setOrder('asc');
    }
  };

  // Função para renderizar ícone de ordenação
  const renderSortIcon = (column: string) => {
    if (orderBy !== column) {
      return <Icon as={ArrowUpDown} boxSize={3} color="gray.400" />;
    }
    if (order === 'asc') {
      return <CaretUp size={14} weight="bold" />;
    }
    return <CaretDown size={14} weight="bold" />;
  };

  // Componente para header com filtro
  const FilterableHeader = ({
    column,
    label,
    type,
    options,
    width,
    sortable = false,
  }: {
    column: string;
    label: string;
    type: 'text' | 'date' | 'select';
    options?: { value: string; label: string }[];
    width?: string;
    sortable?: boolean;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const currentValue = filtrosColuna[column] || '';
    const [localValue, setLocalValue] = useState(currentValue);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sincroniza localValue quando o valor externo muda
    useEffect(() => {
      setLocalValue(currentValue);
    }, [currentValue]);

    const hasFilter = !!filtrosColuna[column];

    const handleApplyFilter = () => {
      handleFiltroColuna(column, localValue);
      setIsOpen(false);
    };

    const handleClearFilter = () => {
      setLocalValue('');
      handleFiltroColuna(column, '');
      setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleApplyFilter();
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    useEffect(() => {
      if (isOpen && inputRef.current && type === 'text') {
        inputRef.current.focus();
      }
    }, [isOpen, type]);

    return (
      <Th position="sticky" top={0} bg="gray.50" zIndex={2} width={width} minW={width}>
        <VStack spacing={0} align="stretch">
          <HStack
            spacing={1}
            justify="center"
            cursor={sortable ? 'pointer' : 'default'}
            onClick={sortable ? () => handleSort(column) : undefined}
            _hover={sortable ? { bg: 'gray.100' } : undefined}
            transition="background 0.2s"
            borderRadius="md"
            px={1}
            minH="24px"
          >
            <Text fontSize="xs" fontWeight="bold">
              {label}
            </Text>
            {sortable && renderSortIcon(column)}
          </HStack>
          <Divider borderColor="gray.300" my={1} />
          <Popover
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            placement="bottom-start"
            closeOnBlur
            strategy="fixed"
          >
            <PopoverTrigger>
              <IconButton
                aria-label={`Filtrar ${label}`}
                icon={<MagnifyingGlass size={14} weight={hasFilter ? 'bold' : 'regular'} />}
                size="xs"
                variant={hasFilter ? 'solid' : 'ghost'}
                colorScheme={hasFilter ? 'blue' : 'gray'}
                onClick={() => setIsOpen(!isOpen)}
              />
            </PopoverTrigger>
            <PopoverContent width="220px" zIndex={1500}>
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader fontSize="sm" fontWeight="semibold">
                Filtrar {label}
              </PopoverHeader>
              <PopoverBody>
                <VStack spacing={2} align="stretch">
                  {type === 'text' && (
                    <InputGroup size="sm">
                      <InputLeftElement>
                        <MagnifyingGlass size={14} color="gray" />
                      </InputLeftElement>
                      <Input
                        ref={inputRef}
                        placeholder="Buscar..."
                        value={localValue}
                        onChange={e => setLocalValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                      />
                    </InputGroup>
                  )}
                  {type === 'date' && (
                    <Input
                      ref={inputRef}
                      type="date"
                      size="sm"
                      value={localValue}
                      onChange={e => setLocalValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                  {type === 'select' && options && (
                    <Select
                      size="sm"
                      value={localValue}
                      onChange={e => setLocalValue(e.target.value)}
                      placeholder="Selecione..."
                    >
                      {options.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  )}
                  <HStack spacing={2} justify="flex-end">
                    <Button size="xs" variant="ghost" onClick={handleClearFilter}>
                      Limpar
                    </Button>
                    <Button size="xs" colorScheme="blue" onClick={handleApplyFilter}>
                      Aplicar
                    </Button>
                  </HStack>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </VStack>
      </Th>
    );
  };

  const { data: dataNfseList, isFetching: isFetchingNfseList } = useQuery<IData<INfse[]>>({
    queryKey: ['NfseList', filtrosCompletos],
    queryFn: getNfseList,
  });

  const { data: dataStats } = useQuery<IDataNoPagination<INfseStats>>({
    queryKey: ['NfseStats', statsParams],
    queryFn: getNfseStats,
  });

  // Query para buscar municípios únicos do backend
  const { data: dataMunicipios } = useQuery<IDataNoPagination<string[]>>({
    queryKey: ['municipios-retencao-nfse', { codi_emp: id }],
    queryFn: getMunicipiosRetencaoNfse,
    enabled: !!id,
  });

  // Lista de municípios únicos do backend
  const municipiosUnicos = useMemo(() => {
    const municipios = dataMunicipios?.data || [];
    const municipiosFiltrados = municipios
      .filter(municipio => municipio && municipio.trim() !== '')
      .filter((municipio, index, array) => array.indexOf(municipio) === index)
      .sort();

    return municipiosFiltrados;
  }, [dataMunicipios]);

  const { items: nfseListRaw, ...pagination } = dataNfseList?.data ?? {
    items: [],
    ...({} as IPagination),
  };

  // Lista de municípios de incidência únicos baseada nos dados da tabela
  const locaisIncidenciaUnicos = useMemo(() => {
    const locais = nfseListRaw
      .map(nota => nota.local_incidencia)
      .filter((local): local is string => !!local && local.trim() !== '');

    const locaisUnicos = Array.from(new Set(locais)).sort();
    return locaisUnicos.map(local => ({ value: local, label: local }));
  }, [nfseListRaw]);

  // Filtragem local por coluna (apenas para campos que não são enviados ao backend)
  // Colunas enviadas ao backend: numero_nfse, chave_nfse, emissor_nome, emissor_cnpj, emissor_im,
  // local_emissao, tomador_nome, tomador_cnpj, tomador_municipio, servico_codigo, servico_descricao,
  // servico_codigo_trib_mun, local_incidencia, discriminacao
  const colunasBackend = new Set([
    'numero_nfse',
    'chave_nfse',
    'emissor_nome',
    'emissor_cnpj',
    'emissor_im',
    'local_emissao',
    'tomador_nome',
    'tomador_cnpj',
    'tomador_municipio',
    'servico_codigo',
    'tributo_nacional',
    'servico_descricao',
    'servico_codigo_trib_mun',
    'local_incidencia',
    'discriminacao',
    'tp_ret_pis_cofins',
  ]);

  const nfseList = useMemo(() => {
    // Filtra apenas colunas que não são enviadas ao backend
    const filtrosLocais = Object.entries(filtrosColuna).filter(
      ([coluna]) => !colunasBackend.has(coluna),
    );

    if (filtrosLocais.length === 0) {
      return nfseListRaw;
    }

    return nfseListRaw.filter(nota => {
      return filtrosLocais.every(([coluna, valor]) => {
        if (!valor || valor === '' || valor === 'todos') return true;

        const valorLower = valor.toLowerCase();

        switch (coluna) {
          case 'data_emissao':
            return nota.data_emissao?.includes(valor);
          case 'data_competencia':
            return nota.data_competencia?.includes(valor);
          case 'trib_issqn':
            return nota.trib_issqn === valor;
          case 'tipo':
            return nota.tipo === valor;
          case 'status':
            if (valor === 'Cancelada') return nota.cancelada === true;
            if (valor === 'Normal') return nota.cancelada !== true;
            return nota.status?.toLowerCase().includes(valorLower);
          case 'valor_serv_prest':
            return String(nota.valor_serv_prest || '').includes(normalizeMonetaryValue(valor));
          case 'valor_bc':
            return String(nota.valor_bc || '').includes(normalizeMonetaryValue(valor));
          case 'aliquota_aplic':
            return String(nota.aliquota_aplic || '').includes(normalizeMonetaryValue(valor));
          // ISS exclusivo (espelha a exibição): retido só em "ISS Ret", próprio
          // só em "ISS Normal". O `|| ''` vira string vazia quando a célula
          // mostra '-' — senão o filtro "0" casaria com linhas sem valor.
          case 'valor_issqn':
            return String(getIssProprio(nota) || '').includes(normalizeMonetaryValue(valor));
          case 'iss_retido':
            return String(getIssRetido(nota) || '').includes(normalizeMonetaryValue(valor));
          // Retenções federais com fallback pros campos legados (mesma regra do DANFSe/PDF)
          case 'irrf':
            return String(nota.v_ret_irrf || nota.irrf || '').includes(
              normalizeMonetaryValue(valor),
            );
          case 'pis':
            return String(nota.v_pis || nota.pis || '').includes(normalizeMonetaryValue(valor));
          case 'cofins':
            return String(nota.v_cofins || nota.cofins || '').includes(
              normalizeMonetaryValue(valor),
            );
          case 'csll':
            return String(nota.v_ret_csll || nota.csll || '').includes(
              normalizeMonetaryValue(valor),
            );
          case 'inss':
            return String(nota.v_ret_inss || nota.inss || '').includes(
              normalizeMonetaryValue(valor),
            );
          default:
            return true;
        }
      });
    });
  }, [nfseListRaw, filtrosColuna]);

  // Atualiza a largura da barra de rolagem superior baseado na tabela
  useEffect(() => {
    const updateTableWidth = () => {
      if (tableContainerRef.current) {
        const table = tableContainerRef.current.querySelector('table');
        if (table) {
          setTableWidth(table.scrollWidth);
        }
      }
    };

    updateTableWidth();
    window.addEventListener('resize', updateTableWidth);
    return () => window.removeEventListener('resize', updateTableWidth);
  }, [nfseList]);

  const stats = dataStats?.data;

  // Obter a data atual do filtro como objeto moment
  const currentFilterDate = moment(filters.get('date_start') ?? moment().startOf('month'));

  // Funções para navegar entre meses
  const handlePrevMonth = () => {
    const prevMonth = currentFilterDate.clone().subtract(1, 'month');
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: prevMonth.startOf('month').format('YYYY-MM-DD'),
        date_end: prevMonth.endOf('month').format('YYYY-MM-DD'),
      }),
    );
  };

  const handleNextMonth = () => {
    const nextMonth = currentFilterDate.clone().add(1, 'month');
    if (!nextMonth.isAfter(moment(), 'month')) {
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: nextMonth.startOf('month').format('YYYY-MM-DD'),
          date_end: nextMonth.endOf('month').format('YYYY-MM-DD'),
        }),
      );
    }
  };

  const handleSelectMonth = (year: number, month: number) => {
    const selectedDate = moment().year(year).month(month);
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: selectedDate.startOf('month').format('YYYY-MM-DD'),
        date_end: selectedDate.endOf('month').format('YYYY-MM-DD'),
      }),
    );
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return moment(dateString).format('DD/MM/YYYY');
  };

  // Verifica se a data de emissão é posterior à data de competência
  const isEmissaoForaDePeriodo = (nota: INfse): boolean => {
    if (!nota.data_emissao || !nota.data_competencia) return false;
    const emissao = moment(nota.data_emissao);
    const competencia = moment(nota.data_competencia);
    // Compara se emissão é posterior à competência (por dia)
    return emissao.isAfter(competencia, 'day');
  };

  const exportToCSV = useCallback(async () => {
    setIsExportingCsv(true);
    toast({
      title: 'Exportando dados...',
      description: 'Aguarde enquanto buscamos todas as notas.',
      status: 'info',
      duration: null,
      isClosable: false,
      id: 'export-csv-loading',
    });

    try {
      // Busca todos os dados sem paginação, com os MESMOS filtros da tabela
      // (inclusive cancelamento, filtros de imposto e filtros de coluna) —
      // o export reflete exatamente o que o usuário está vendo.
      const filtrosParaExportar: Record<string, any> = { ...filtrosCompletos };
      delete filtrosParaExportar.page;
      delete filtrosParaExportar.size;

      const allNfse = await getAllNfse(filtrosParaExportar);

      toast.close('export-csv-loading');

      if (!allNfse || allNfse.length === 0) {
        toast({
          title: 'Nenhuma nota encontrada',
          description: 'Não há notas para exportar com os filtros selecionados.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        setIsExportingCsv(false);
        return;
      }

      const headers = [
        'Data Nota',
        'Competência',
        'Número NF',
        'Nome Emitente',
        'CNPJ/CPF Emitente',
        'Cidade Emitente',
        'Nome Tomador',
        'CNPJ/CPF Tomador',
        'Cidade Tomador',
        'M. Incidência',
        'Tipo',
        'Vr. Contábil',
        'Base Cálculo',
        'Aliq ISS',
        'ISS Normal',
        'ISS Ret',
        'IRRF',
        'PIS',
        'COFINS',
        'Ret. PIS/COF',
        'CSLL',
        'INSS',
        'Cód. Trib. Nacional',
        'Desc. Trib. Nacional',
        'Chave NFS-e',
        'IM Emitente',
        'Discriminação',
        'Cód Trib. Município',
        'Município Incidência',
        'Status da Nota',
        'Situação',
        'IBS Estadual',
        'IBS Municipal',
        'CBS',
        'Total IBS/CBS',
      ];

      // Limpa texto removendo quebras de linha e espaços extras (sem truncar — exporta o conteúdo completo)
      const cleanText = (value: string | null | undefined) => {
        if (!value) return '';
        return String(value)
          .replace(/[\r\n]+/g, ' ') // Remove quebras de linha
          .replace(/\s+/g, ' ') // Remove espaços múltiplos
          .trim();
      };

      const escapeCSV = (value: string | number | null | undefined) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes(';') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // Número no padrão do Excel pt-BR (vírgula decimal, sem separador de
      // milhar). Exportar cru ("1234.56") faz o Excel tratar como texto e as
      // somas/pivôs quebram.
      const numExcel = (value: number | null | undefined) =>
        value === null || value === undefined ? '' : String(value).replace('.', ',');

      // Campo numérico longo (chave de 50 dígitos): sem proteção o Excel
      // converte pra notação científica e ZERA os dígitos após o 15º.
      // O formato ="..." força texto preservando todos os dígitos.
      const textoExcel = (value: string | null | undefined) => (value ? `="${value}"` : '');

      const rows = allNfse.map(nota => {
        const nomeEmitente = nota.emissor_nome || '';
        const cnpjCpfEmitente = nota.emissor_cnpj
          ? formatCNPJ(nota.emissor_cnpj)
          : (nota.emissor_cpf && formatCPF(nota.emissor_cpf)) || '';
        const cidadeEmitente = nota.local_emissao || '';

        const nomeTomador = nota.tomador_nome || '';
        const cnpjCpfTomador = nota.tomador_cnpj
          ? formatCNPJ(nota.tomador_cnpj)
          : (nota.tomador_cpf && formatCPF(nota.tomador_cpf)) || '';
        const cidadeTomador = nota.tomador_nome_municipio
          ? `${nota.tomador_nome_municipio}${nota.tomador_uf ? ` - ${nota.tomador_uf}` : ''}`
          : '';

        // ISS exclusivo (mesma regra da tabela/PDF via nfseCampos):
        // retido só em "ISS Ret" — somar as duas colunas não dobra o ISS.
        const issNormal = getIssProprio(nota);
        const issRet = getIssRetido(nota);

        // M. Incidência: mesmo rótulo exibido na tela (getTribIssqnLabel)
        const mIncidencia = (() => {
          switch (nota.trib_issqn) {
            case '1':
            case '2':
              return nota.local_incidencia || 'Município';
            case '3':
              return 'Isento';
            case '4':
              return 'Imune';
            case '5':
              return 'Susp. Judicial';
            case '6':
              return 'Susp. Admin.';
            default:
              return '-';
          }
        })();

        // PIS/COFINS são débito de apuração própria (NT-008 §2.1.9): sempre
        // exportados; a retenção é o indicador da coluna Ret. PIS/COF.
        const pisValor = nota.v_pis || nota.pis || 0;
        const cofinsValor = nota.v_cofins || nota.cofins || 0;
        const retPisCofinsLabel = getRetPisCofinsLabel(nota.tp_ret_pis_cofins);

        // Status / situação da nota (indicador ativa/cancelada/substituída)
        const foiSubstituida =
          !!nota.chave_substituta &&
          (nota.motivo_cancelamento || '').toLowerCase().includes('substitu');
        let statusNota = 'Autorizada';
        if (nota.cancelada) statusNota = foiSubstituida ? 'Substituída' : 'Cancelada';
        const situacaoNota = nota.cancelada ? 'Cancelada' : 'Ativa';

        // IBS/CBS (Reforma Tributária): valores do grupo IBSCBS que o extrator grava em
        // dados_completos.danfse.ibscbs (mesma fonte do DANFSe). Nota sem o grupo fica em branco.
        const ibsCbs = nota.dados_completos?.danfse?.ibscbs || {};
        const numXml = (value: string | undefined) =>
          value === undefined || value === '' || Number.isNaN(Number(value))
            ? ''
            : numExcel(Number(value));
        const ibsEstadual = numXml(ibsCbs.v_ibs_uf);
        const ibsMunicipal = numXml(ibsCbs.v_ibs_mun);
        const cbsValor = numXml(ibsCbs.v_cbs);
        const totalIbsCbs =
          ibsCbs.v_ibs_tot || ibsCbs.v_cbs
            ? numExcel(
                Number(((Number(ibsCbs.v_ibs_tot) || 0) + (Number(ibsCbs.v_cbs) || 0)).toFixed(2)),
              )
            : '';

        return [
          formatDate(nota.data_emissao),
          formatDate(nota.data_competencia),
          nota.numero_nfse || '',
          cleanText(nomeEmitente),
          cnpjCpfEmitente,
          cleanText(cidadeEmitente),
          cleanText(nomeTomador),
          cnpjCpfTomador,
          cleanText(cidadeTomador),
          mIncidencia,
          nota.tipo === 'prestado' ? 'Prestada' : 'Tomada',
          numExcel(nota.valor_serv_prest || 0),
          numExcel(nota.valor_bc || 0),
          nota.aliquota_aplic ? `${numExcel(nota.aliquota_aplic)}%` : '',
          numExcel(issNormal),
          numExcel(issRet),
          numExcel(nota.v_ret_irrf || nota.irrf || 0),
          numExcel(pisValor),
          numExcel(cofinsValor),
          retPisCofinsLabel,
          numExcel(nota.v_ret_csll || nota.csll || 0),
          numExcel(nota.v_ret_inss || nota.inss || 0),
          nota.servico_codigo || '',
          cleanText(nota.tributo_nacional),
          textoExcel(nota.chave_nfse),
          nota.emissor_im || nota.prestador_im || '',
          cleanText(nota.servico_descricao),
          nota.servico_codigo_trib_mun || '',
          cleanText(nota.local_incidencia),
          statusNota,
          situacaoNota,
          ibsEstadual,
          ibsMunicipal,
          cbsValor,
          totalIbsCbs,
        ].map(escapeCSV);
      });

      const csvContent = [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `nfse_${id}_${moment(filters.get('date_start')).format('YYYY-MM')}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Exportação concluída!',
        description: `${allNfse.length} notas exportadas com sucesso.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao exportar CSV:', error);
      toast.close('export-csv-loading');
      toast({
        title: 'Erro ao exportar',
        description: 'Ocorreu um erro ao exportar as notas. Tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsExportingCsv(false);
    }
  }, [id, filters, filtrosCompletos, getAllNfse, toast]);

  // Função para exportar todas as notas em ZIP
  const handleExportAllPdfs = async () => {
    if (nfseList.length === 0) return;

    setIsExportingZip(true);
    try {
      const zipFileName = `NFSe_${id}_${moment(filters.get('date_start')).format('YYYY-MM')}.zip`;
      await exportNfsesToZip(nfseList, zipFileName);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao exportar PDFs:', error);
    } finally {
      setIsExportingZip(false);
    }
  };

  // Função para exportar tabela como PDF com colunas selecionadas
  const handleExportTablePdf = async (selectedColumns: string[], exportMode: ExportMode) => {
    setIsExportingPdf(true);
    toast({
      title: 'Gerando PDF...',
      description: 'Aguarde enquanto buscamos todas as notas.',
      status: 'info',
      duration: null,
      isClosable: false,
      id: 'export-pdf-loading',
    });

    try {
      // Mesmos filtros da tabela (menos paginação) — igual ao export CSV
      const filtrosParaExportar: Record<string, any> = { ...filtrosCompletos };
      delete filtrosParaExportar.page;
      delete filtrosParaExportar.size;

      const allNfse = await getAllNfse(filtrosParaExportar);

      toast.close('export-pdf-loading');

      if (!allNfse || allNfse.length === 0) {
        toast({
          title: 'Nenhuma nota encontrada',
          description: 'Não há notas para exportar com os filtros selecionados.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        setIsExportingPdf(false);
        return;
      }

      const dateStart = filters.get('date_start') || '';
      const periodo = dateStart
        ? new Date(`${dateStart}T00:00:00`).toLocaleDateString('pt-BR', {
            month: 'long',
            year: 'numeric',
          })
        : '';

      await exportNfseTablePdf({
        notas: allNfse,
        stats,
        selectedColumns,
        companyName: company?.nome || company?.razao_social || 'Empresa',
        companyCnpj: company?.cnpj ? formatCNPJ(company.cnpj) : '-',
        companyId: id || '',
        periodo,
        exportMode,
      });

      const descExport =
        exportMode === 'separado'
          ? `${allNfse.length} notas exportadas em 2 PDFs (tomadas e prestadas).`
          : `${allNfse.length} notas exportadas.`;

      toast({
        title: 'PDF gerado com sucesso!',
        description: descExport,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      setIsExportPdfModalOpen(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao exportar PDF:', error);
      toast.close('export-pdf-loading');
      toast({
        title: 'Erro ao exportar PDF',
        description: 'Ocorreu um erro ao gerar o PDF. Tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const getEmptyMessage = (): string => {
    if (isFetchingNfseList) return 'Carregando NFS-e...';
    if (Object.keys(filtrosColuna).length > 0) {
      return 'Nenhum resultado encontrado para os filtros aplicados.';
    }
    return 'Nenhuma NFS-e encontrada para esta empresa.';
  };

  const getTribIssqnLabel = (
    codigo: string | undefined,
    localIncidencia: string | undefined,
  ): string => {
    switch (codigo) {
      case '1':
      case '2':
        return localIncidencia || 'Município';
      case '3':
        return 'Isento';
      case '4':
        return 'Imune';
      case '5':
        return 'Susp. Judicial';
      case '6':
        return 'Susp. Admin.';
      default:
        return '-';
    }
  };

  const getTribIssqnTooltip = (codigo: string | undefined): string => {
    switch (codigo) {
      case '1':
        return 'Tributado no município do prestador';
      case '2':
        return 'Tributado no município do tomador';
      case '3':
        return 'Isento';
      case '4':
        return 'Imune / Não incidência';
      case '5':
        return 'Exigibilidade suspensa por decisão judicial';
      case '6':
        return 'Exigibilidade suspensa por procedimento administrativo';
      default:
        return '';
    }
  };

  const getTribIssqnColor = (codigo: string | undefined): string => {
    switch (codigo) {
      case '1':
      case '2':
        return 'blue';
      case '3':
        return 'green';
      case '4':
        return 'purple';
      case '5':
      case '6':
        return 'orange';
      default:
        return 'gray';
    }
  };

  return (
    <PageBox>
      <div className="flex content-start pb-5 md:w-full lg:content-center ">
        <ReturnButton previousPage="modulesHome/integration/nfseManagement" />

        <div className="h-full">
          <div className="flex items-center justify-center rounded-full bg-[#E3F2FD] p-3">
            <FaFileInvoice color="#1976D2" className="size-8" />
          </div>
        </div>
        <div className="flex w-full flex-col justify-center gap-1 pl-2">
          <Text fontWeight="bold" fontSize="16px">
            NFS-e - {company?.nome || company?.razao_social || 'Empresa'}
          </Text>
          <Text fontSize="12px" color="gray.500">
            Cód: {id || company?.external_id} | CNPJ:{' '}
            {company?.cnpj ? formatCNPJ(company.cnpj) : '-'}
          </Text>
        </div>
      </div>

      {/* Cards de Estatísticas - Insights */}
      <div className="grid grid-cols-1 gap-4 pb-4 md:grid-cols-2 lg:grid-cols-5">
        {/* Card 1: Quantidade de Notas */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Quantidade de Notas</StatLabel>
              <StatNumber fontSize={{ base: 'lg', md: 'xl', lg: '2xl' }}>
                {stats?.total || 0}
              </StatNumber>
              <StatHelpText>
                <div className="flex flex-col gap-1 pt-1">
                  <div className="flex justify-between">
                    <Badge colorScheme="green" variant="subtle" fontSize="xs">
                      Prestadas
                    </Badge>
                    <span className="font-semibold">{stats?.total_prestados || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                      Tomadas
                    </Badge>
                    <span className="font-semibold">{stats?.total_tomados || 0}</span>
                  </div>
                </div>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Card 2: Status das Notas */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Status das Notas</StatLabel>
              <StatHelpText>
                <div className="flex flex-col gap-1 pt-2">
                  <div className="flex justify-between">
                    <Badge colorScheme="green" variant="subtle" fontSize="xs">
                      Ativas
                    </Badge>
                    <span className="font-semibold">
                      {stats?.total_ativas ||
                        (stats?.total || 0) -
                          (stats?.total_canceladas || 0) -
                          (stats?.total_substituidas || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <Badge colorScheme="red" variant="subtle" fontSize="xs">
                      Canceladas
                    </Badge>
                    <span className="font-semibold">{stats?.total_canceladas || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                      Substituídas
                    </Badge>
                    <span className="font-semibold">{stats?.total_substituidas || 0}</span>
                  </div>
                </div>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Card 3: Notas por Retenção */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Notas por Retenção</StatLabel>
              <StatHelpText>
                <div className="flex flex-col gap-1 pt-2">
                  <button
                    type="button"
                    className="-mx-1 flex w-full cursor-pointer justify-between rounded px-1 hover:bg-gray-100"
                    onClick={() =>
                      setFiltroTipoRetencao(prev => (prev === 'federal' ? '' : 'federal'))
                    }
                  >
                    <Badge
                      colorScheme="orange"
                      variant={filtroTipoRetencao === 'federal' ? 'solid' : 'subtle'}
                      fontSize="xs"
                    >
                      RET. FEDERAL
                    </Badge>
                    <span className="font-semibold">{stats?.total_com_retencao_federal || 0}</span>
                  </button>
                  <button
                    type="button"
                    className="-mx-1 flex w-full cursor-pointer justify-between rounded px-1 hover:bg-gray-100"
                    onClick={() =>
                      setFiltroTipoRetencao(prev => (prev === 'pis_cofins' ? '' : 'pis_cofins'))
                    }
                  >
                    <Badge
                      colorScheme="red"
                      variant={filtroTipoRetencao === 'pis_cofins' ? 'solid' : 'subtle'}
                      fontSize="xs"
                    >
                      RET. PIS/COFINS
                    </Badge>
                    <span className="font-semibold">
                      {stats?.total_com_retencao_pis_cofins_tp2 || 0}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="-mx-1 flex w-full cursor-pointer justify-between rounded px-1 hover:bg-gray-100"
                    onClick={() =>
                      setFiltroTipoRetencao(prev => (prev === 'municipal' ? '' : 'municipal'))
                    }
                  >
                    <Badge
                      colorScheme="purple"
                      variant={filtroTipoRetencao === 'municipal' ? 'solid' : 'subtle'}
                      fontSize="xs"
                    >
                      RET. MUNICIPAL
                    </Badge>
                    <span className="font-semibold">
                      {stats?.total_com_retencao_municipal || 0}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="-mx-1 flex w-full cursor-pointer justify-between rounded px-1 hover:bg-gray-100"
                    onClick={() =>
                      setFiltroTipoRetencao(prev => (prev === 'sem_retencao' ? '' : 'sem_retencao'))
                    }
                  >
                    <Badge
                      colorScheme="gray"
                      variant={filtroTipoRetencao === 'sem_retencao' ? 'solid' : 'subtle'}
                      fontSize="xs"
                    >
                      SEM RETENÇÃO
                    </Badge>
                    <span className="font-semibold">{stats?.total_sem_retencao || 0}</span>
                  </button>
                </div>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Card 4: Valor Total - Prestadas */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Valor Notas Prestadas</StatLabel>
              <StatNumber fontSize={{ base: 'md', md: 'lg', lg: 'xl' }} color="green.500">
                {formatCurrency(stats?.valor_total_prestados || 0)}
              </StatNumber>
              <StatHelpText>{stats?.total_prestados || 0} nota(s) prestada(s)</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Card 5: Valor Total - Tomadas */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Valor Notas Tomadas</StatLabel>
              <StatNumber fontSize={{ base: 'md', md: 'lg', lg: 'xl' }} color="blue.500">
                {formatCurrency(stats?.valor_total_tomados || 0)}
              </StatNumber>
              <StatHelpText>{stats?.total_tomados || 0} nota(s) tomada(s)</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </div>

      <FormBox>
        <div className="flex flex-col gap-4 p-4">
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Text fontWeight="bold" fontSize="14px">
              Lista de NFS-e
            </Text>
            <HStack spacing={3}>
              <Button
                leftIcon={<Icon as={FileText} boxSize={4} />}
                variant="outline"
                colorScheme="red"
                size="sm"
                onClick={() => setIsExportPdfModalOpen(true)}
                isDisabled={!nfseList || nfseList.length === 0}
              >
                Exportar PDF
              </Button>
              <Button
                leftIcon={<Icon as={Download} boxSize={4} />}
                variant="outline"
                colorScheme="green"
                size="sm"
                onClick={exportToCSV}
                isLoading={isExportingCsv}
                isDisabled={!nfseList || nfseList.length === 0}
              >
                Exportar CSV
              </Button>
              <Popover placement="bottom-end">
                <PopoverTrigger>
                  <Button variant="outline" colorScheme="blue" position="relative" size="sm">
                    Filtros Avançados
                    {contadorFiltrosAtivos > 0 && (
                      <Badge
                        position="absolute"
                        top="-8px"
                        right="-8px"
                        colorScheme="blue"
                        borderRadius="full"
                      >
                        {contadorFiltrosAtivos}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent width="650px" zIndex={1500}>
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverHeader fontWeight="bold" borderBottomWidth="1px">
                    <Flex justify="space-between" align="center" pr={6}>
                      <Text>Filtros de Impostos e Retenção</Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={handleLimparFiltrosImpostos}
                      >
                        Limpar Todos
                      </Button>
                    </Flex>
                  </PopoverHeader>
                  <PopoverBody maxH="500px" overflowY="auto" p={4}>
                    <VStack align="stretch" spacing={5}>
                      {/* Primeira linha: Tipo de Nota e CNPJ */}
                      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.600">
                            Tipo de Nota
                          </Text>
                          <Select
                            value={filtroTipo}
                            onChange={e =>
                              setFiltroTipo(e.target.value as '' | 'prestado' | 'tomado')
                            }
                            size="sm"
                            bg="white"
                          >
                            <option value="">Todos</option>
                            <option value="prestado">Prestado</option>
                            <option value="tomado">Tomado</option>
                          </Select>
                        </Box>
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.600">
                            CNPJ (Prestador/Tomador)
                          </Text>
                          <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                              <MagnifyingGlass size={16} color="gray" />
                            </InputLeftElement>
                            <Input
                              placeholder="Digite o CNPJ..."
                              value={filtroCnpj}
                              onChange={e => setFiltroCnpj(e.target.value)}
                              bg="white"
                            />
                          </InputGroup>
                        </Box>
                      </Grid>

                      {/* Segunda linha: Status e Filtros de Retenção */}
                      <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.600">
                            Status
                          </Text>
                          <Select
                            value={filtroCancelamento}
                            onChange={e =>
                              setFiltroCancelamento(
                                e.target.value as
                                  | 'todos'
                                  | 'canceladas'
                                  | 'substituidas'
                                  | 'ativas',
                              )
                            }
                            size="sm"
                            bg="white"
                          >
                            <option value="todos">Todas</option>
                            <option value="canceladas">Canceladas</option>
                            <option value="substituidas">Substituídas</option>
                            <option value="ativas">Ativas</option>
                          </Select>
                        </Box>
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.600">
                            Município Ret.
                          </Text>
                          <Select
                            value={filtroMunicipioRetencao}
                            onChange={e => setFiltroMunicipioRetencao(e.target.value)}
                            size="sm"
                            bg="white"
                          >
                            <option value="todos">Todos</option>
                            {municipiosUnicos.map(municipio => (
                              <option key={`municipio-${municipio}`} value={municipio}>
                                {municipio}
                              </option>
                            ))}
                          </Select>
                        </Box>
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.600">
                            Ret. Federal
                          </Text>
                          <Select
                            value={filtroRetencaoFederal}
                            onChange={e =>
                              setFiltroRetencaoFederal(
                                e.target.value as '' | 'tomador' | 'prestador',
                              )
                            }
                            size="sm"
                            bg="white"
                          >
                            <option value="">Todos</option>
                            <option value="tomador">Com retenção</option>
                            <option value="prestador">Sem retenção</option>
                          </Select>
                        </Box>
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.600">
                            Ret. Municipal
                          </Text>
                          <Select
                            value={filtroRetencaoMunicipal}
                            onChange={e =>
                              setFiltroRetencaoMunicipal(
                                e.target.value as '' | 'tomador' | 'prestador',
                              )
                            }
                            size="sm"
                            bg="white"
                          >
                            <option value="">Todos</option>
                            <option value="tomador">Com retenção</option>
                            <option value="prestador">Sem retenção</option>
                          </Select>
                        </Box>
                      </Grid>

                      <Divider />

                      {/* Seção de Filtros de Impostos Individuais */}
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" mb={3} color="blue.600">
                          FILTROS POR IMPOSTO
                        </Text>
                        <Grid templateColumns="repeat(5, 1fr)" gap={3}>
                          {/* PIS */}
                          <Box
                            bg="gray.50"
                            p={2}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={filtrosImpostos.pis !== 'todos' ? 'blue.300' : 'gray.200'}
                          >
                            <Text fontSize="xs" fontWeight="bold" mb={1} textAlign="center">
                              PIS
                            </Text>
                            <VStack spacing={0}>
                              <Checkbox
                                isChecked={filtrosImpostos.pis === 'com'}
                                onChange={() => handleToggleFiltroImposto('pis', 'com')}
                                size="sm"
                              >
                                <Text fontSize="xs">Com</Text>
                              </Checkbox>
                              <Checkbox
                                isChecked={filtrosImpostos.pis === 'sem'}
                                onChange={() => handleToggleFiltroImposto('pis', 'sem')}
                                size="sm"
                              >
                                <Text fontSize="xs">Sem</Text>
                              </Checkbox>
                            </VStack>
                          </Box>

                          {/* COFINS */}
                          <Box
                            bg="gray.50"
                            p={2}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={
                              filtrosImpostos.cofins !== 'todos' ? 'blue.300' : 'gray.200'
                            }
                          >
                            <Text fontSize="xs" fontWeight="bold" mb={1} textAlign="center">
                              COFINS
                            </Text>
                            <VStack spacing={0}>
                              <Checkbox
                                isChecked={filtrosImpostos.cofins === 'com'}
                                onChange={() => handleToggleFiltroImposto('cofins', 'com')}
                                size="sm"
                              >
                                <Text fontSize="xs">Com</Text>
                              </Checkbox>
                              <Checkbox
                                isChecked={filtrosImpostos.cofins === 'sem'}
                                onChange={() => handleToggleFiltroImposto('cofins', 'sem')}
                                size="sm"
                              >
                                <Text fontSize="xs">Sem</Text>
                              </Checkbox>
                            </VStack>
                          </Box>

                          {/* IRPJ */}
                          <Box
                            bg="gray.50"
                            p={2}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={filtrosImpostos.irpj !== 'todos' ? 'blue.300' : 'gray.200'}
                          >
                            <Text fontSize="xs" fontWeight="bold" mb={1} textAlign="center">
                              IRPJ
                            </Text>
                            <VStack spacing={0}>
                              <Checkbox
                                isChecked={filtrosImpostos.irpj === 'com'}
                                onChange={() => handleToggleFiltroImposto('irpj', 'com')}
                                size="sm"
                              >
                                <Text fontSize="xs">Com</Text>
                              </Checkbox>
                              <Checkbox
                                isChecked={filtrosImpostos.irpj === 'sem'}
                                onChange={() => handleToggleFiltroImposto('irpj', 'sem')}
                                size="sm"
                              >
                                <Text fontSize="xs">Sem</Text>
                              </Checkbox>
                            </VStack>
                          </Box>

                          {/* CSLL */}
                          <Box
                            bg="gray.50"
                            p={2}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={filtrosImpostos.csll !== 'todos' ? 'blue.300' : 'gray.200'}
                          >
                            <Text fontSize="xs" fontWeight="bold" mb={1} textAlign="center">
                              CSLL
                            </Text>
                            <VStack spacing={0}>
                              <Checkbox
                                isChecked={filtrosImpostos.csll === 'com'}
                                onChange={() => handleToggleFiltroImposto('csll', 'com')}
                                size="sm"
                              >
                                <Text fontSize="xs">Com</Text>
                              </Checkbox>
                              <Checkbox
                                isChecked={filtrosImpostos.csll === 'sem'}
                                onChange={() => handleToggleFiltroImposto('csll', 'sem')}
                                size="sm"
                              >
                                <Text fontSize="xs">Sem</Text>
                              </Checkbox>
                            </VStack>
                          </Box>

                          {/* IR */}
                          <Box
                            bg="gray.50"
                            p={2}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={filtrosImpostos.ir !== 'todos' ? 'blue.300' : 'gray.200'}
                          >
                            <Text fontSize="xs" fontWeight="bold" mb={1} textAlign="center">
                              IR
                            </Text>
                            <VStack spacing={0}>
                              <Checkbox
                                isChecked={filtrosImpostos.ir === 'com'}
                                onChange={() => handleToggleFiltroImposto('ir', 'com')}
                                size="sm"
                              >
                                <Text fontSize="xs">Com</Text>
                              </Checkbox>
                              <Checkbox
                                isChecked={filtrosImpostos.ir === 'sem'}
                                onChange={() => handleToggleFiltroImposto('ir', 'sem')}
                                size="sm"
                              >
                                <Text fontSize="xs">Sem</Text>
                              </Checkbox>
                            </VStack>
                          </Box>

                          {/* IRRF */}
                          <Box
                            bg="gray.50"
                            p={2}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={filtrosImpostos.irrf !== 'todos' ? 'blue.300' : 'gray.200'}
                          >
                            <Text fontSize="xs" fontWeight="bold" mb={1} textAlign="center">
                              IRRF
                            </Text>
                            <VStack spacing={0}>
                              <Checkbox
                                isChecked={filtrosImpostos.irrf === 'com'}
                                onChange={() => handleToggleFiltroImposto('irrf', 'com')}
                                size="sm"
                              >
                                <Text fontSize="xs">Com</Text>
                              </Checkbox>
                              <Checkbox
                                isChecked={filtrosImpostos.irrf === 'sem'}
                                onChange={() => handleToggleFiltroImposto('irrf', 'sem')}
                                size="sm"
                              >
                                <Text fontSize="xs">Sem</Text>
                              </Checkbox>
                            </VStack>
                          </Box>

                          {/* ISSQN */}
                          <Box
                            bg="gray.50"
                            p={2}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={
                              filtrosImpostos.issqn !== 'todos' ? 'blue.300' : 'gray.200'
                            }
                          >
                            <Text fontSize="xs" fontWeight="bold" mb={1} textAlign="center">
                              ISSQN
                            </Text>
                            <VStack spacing={0}>
                              <Checkbox
                                isChecked={filtrosImpostos.issqn === 'com'}
                                onChange={() => handleToggleFiltroImposto('issqn', 'com')}
                                size="sm"
                              >
                                <Text fontSize="xs">Com</Text>
                              </Checkbox>
                              <Checkbox
                                isChecked={filtrosImpostos.issqn === 'sem'}
                                onChange={() => handleToggleFiltroImposto('issqn', 'sem')}
                                size="sm"
                              >
                                <Text fontSize="xs">Sem</Text>
                              </Checkbox>
                            </VStack>
                          </Box>

                          {/* INSS */}
                          <Box
                            bg="gray.50"
                            p={2}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={filtrosImpostos.inss !== 'todos' ? 'blue.300' : 'gray.200'}
                          >
                            <Text fontSize="xs" fontWeight="bold" mb={1} textAlign="center">
                              INSS
                            </Text>
                            <VStack spacing={0}>
                              <Checkbox
                                isChecked={filtrosImpostos.inss === 'com'}
                                onChange={() => handleToggleFiltroImposto('inss', 'com')}
                                size="sm"
                              >
                                <Text fontSize="xs">Com</Text>
                              </Checkbox>
                              <Checkbox
                                isChecked={filtrosImpostos.inss === 'sem'}
                                onChange={() => handleToggleFiltroImposto('inss', 'sem')}
                                size="sm"
                              >
                                <Text fontSize="xs">Sem</Text>
                              </Checkbox>
                            </VStack>
                          </Box>

                          {/* ISS */}
                          <Box
                            bg="gray.50"
                            p={2}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={filtrosImpostos.iss !== 'todos' ? 'blue.300' : 'gray.200'}
                          >
                            <Text fontSize="xs" fontWeight="bold" mb={1} textAlign="center">
                              ISS
                            </Text>
                            <VStack spacing={0}>
                              <Checkbox
                                isChecked={filtrosImpostos.iss === 'com'}
                                onChange={() => handleToggleFiltroImposto('iss', 'com')}
                                size="sm"
                              >
                                <Text fontSize="xs">Com</Text>
                              </Checkbox>
                              <Checkbox
                                isChecked={filtrosImpostos.iss === 'sem'}
                                onChange={() => handleToggleFiltroImposto('iss', 'sem')}
                                size="sm"
                              >
                                <Text fontSize="xs">Sem</Text>
                              </Checkbox>
                            </VStack>
                          </Box>
                        </Grid>
                      </Box>
                    </VStack>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
              <MonthNavigator
                currentDate={currentFilterDate}
                onPrevMonth={handlePrevMonth}
                onNextMonth={handleNextMonth}
                onSelectMonth={handleSelectMonth}
              />
            </HStack>
          </Flex>

          {/* Tabela de NFS-e */}
          {/* Barra de rolagem superior sincronizada */}
          <Box
            ref={topScrollRef}
            overflowX="auto"
            overflowY="hidden"
            onScroll={handleTopScroll}
            css={{
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },
            }}
          >
            <Box width={`${tableWidth}px`} height="1px" />
          </Box>
          <TableContainer
            ref={tableContainerRef}
            maxH="70vh"
            overflowY="auto"
            onScroll={handleTableScroll}
          >
            <Table
              variant="simple"
              size="sm"
              sx={{
                'th, td': {
                  borderRight: '1px solid',
                  borderColor: 'gray.200',
                },
                'th:last-child, td:last-child': {
                  borderRight: 'none',
                },
              }}
            >
              <Thead bg="gray.50">
                <Tr>
                  <Th
                    position="sticky"
                    top={0}
                    bg="gray.50"
                    zIndex={1}
                    textAlign="center"
                    width="80px"
                  >
                    <VStack spacing={0} align="stretch">
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        minH="24px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        Ações
                      </Text>
                      <Divider borderColor="gray.300" my={1} />
                      <Tooltip label="Exportar todas as notas em ZIP" placement="top">
                        <IconButton
                          aria-label="Exportar todas em ZIP"
                          icon={<Icon as={FileArchive} boxSize={4} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={handleExportAllPdfs}
                          isLoading={isExportingZip}
                          isDisabled={nfseList.length === 0}
                        />
                      </Tooltip>
                    </VStack>
                  </Th>
                  <FilterableHeader column="data_emissao" label="Data Nota" type="date" sortable />
                  <FilterableHeader
                    column="data_competencia"
                    label="Competência"
                    type="date"
                    sortable
                  />
                  <FilterableHeader column="numero_nfse" label="Número NF" type="text" sortable />
                  <FilterableHeader
                    column="emissor_nome"
                    label="Nome Emitente"
                    type="text"
                    sortable
                  />
                  <FilterableHeader
                    column="emissor_cnpj"
                    label="CNPJ/CPF Emitente"
                    type="text"
                    sortable
                  />
                  <FilterableHeader
                    column="local_emissao"
                    label="Cidade Emitente"
                    type="text"
                    sortable
                  />
                  <FilterableHeader
                    column="tomador_nome"
                    label="Nome Tomador"
                    type="text"
                    sortable
                  />
                  <FilterableHeader
                    column="tomador_cnpj"
                    label="CNPJ/CPF Tomador"
                    type="text"
                    sortable
                  />
                  <FilterableHeader
                    column="tomador_municipio"
                    label="Cidade Tomador"
                    type="text"
                    sortable
                  />
                  <FilterableHeader
                    column="local_incidencia"
                    label="M. Incidência"
                    type="select"
                    sortable
                    options={locaisIncidenciaUnicos}
                  />
                  <FilterableHeader
                    column="tipo"
                    label="Tipo"
                    type="select"
                    sortable
                    options={[
                      { value: 'prestado', label: 'Prestada' },
                      { value: 'tomado', label: 'Tomada' },
                    ]}
                  />
                  <FilterableHeader
                    column="valor_serv_prest"
                    label="Vr. Contábil"
                    type="text"
                    sortable
                  />
                  <FilterableHeader column="valor_bc" label="Base Cálculo" type="text" sortable />
                  <FilterableHeader column="aliquota_aplic" label="Aliq ISS" type="text" sortable />
                  <FilterableHeader column="valor_issqn" label="ISS Normal" type="text" sortable />
                  <FilterableHeader column="iss_retido" label="ISS Ret" type="text" sortable />
                  <FilterableHeader column="irrf" label="IRRF" type="text" sortable />
                  <FilterableHeader column="pis" label="PIS" type="text" sortable />
                  <FilterableHeader column="cofins" label="COFINS" type="text" sortable />
                  {/* Opções espelham o que a célula exibe (getRetPisCofinsLabel,
                      tabela oficial da NT-007). Match EXATO por código no backend;
                      o agregado "com retenção" do card usa o parâmetro próprio
                      filtro_com_retencao_pis_cofins. Códigos 0/1/2/3/4/8 existem
                      na base. */}
                  <FilterableHeader
                    column="tp_ret_pis_cofins"
                    label="Ret. PIS/COF"
                    type="select"
                    options={[
                      { value: '1', label: 'RETIDO (cód. 1 — PIS/COFINS)' },
                      { value: '3', label: 'RETIDO (cód. 3 — PIS/COFINS/CSLL)' },
                      { value: '4', label: 'RETIDO (cód. 4 — PIS/COFINS, CSLL não)' },
                      { value: '2', label: 'NÃO RETIDO (cód. 2)' },
                      { value: '0', label: 'NÃO RETIDO (cód. 0)' },
                      { value: '8', label: 'NÃO RETIDO (cód. 8 — só CSLL retida)' },
                    ]}
                    sortable
                  />
                  <FilterableHeader column="csll" label="CSLL" type="text" sortable />
                  <FilterableHeader column="inss" label="INSS" type="text" sortable />
                  {/* servico_codigo é o Código de Tributação Nacional (não CNAE) — NT-008 */}
                  <FilterableHeader
                    column="servico_codigo"
                    label="Cód. Trib. Nacional"
                    type="text"
                    sortable
                  />
                  <FilterableHeader
                    column="tributo_nacional"
                    label="Desc. Trib. Nacional"
                    type="text"
                    sortable
                  />
                  <FilterableHeader column="chave_nfse" label="Chave NFS-e" type="text" sortable />
                  <FilterableHeader column="emissor_im" label="IM Emitente" type="text" sortable />
                  <FilterableHeader
                    column="discriminacao"
                    label="Discriminação"
                    type="text"
                    sortable
                  />
                  <FilterableHeader
                    column="servico_codigo_trib_mun"
                    label="Cód Trib. Município"
                    type="text"
                    sortable
                  />
                  {/* local_incidencia é o município de incidência do ISSQN (NT-008),
                      não uma descrição de código municipal. Chave própria pro filtro
                      não brigar com o select "M. Incidência" (mesmo campo). */}
                  <FilterableHeader
                    column="municipio_incidencia"
                    label="Município Incidência"
                    type="text"
                    sortable
                  />
                </Tr>
              </Thead>
              <Tbody>
                {nfseList.length === 0 ? (
                  <Tr>
                    <Td colSpan={30} textAlign="center" py={10}>
                      <VStack spacing={4}>
                        <Text color="gray.500" fontSize="lg">
                          {getEmptyMessage()}
                        </Text>
                        {Object.keys(filtrosColuna).length > 0 && (
                          <Button
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => setFiltrosColuna({})}
                          >
                            Limpar filtros
                          </Button>
                        )}
                      </VStack>
                    </Td>
                  </Tr>
                ) : (
                  nfseList.map(nota => {
                    const foraDePeriodo = isEmissaoForaDePeriodo(nota);
                    const getBgColor = () => {
                      if (nota.cancelada) return 'red.50';
                      if (foraDePeriodo) return 'orange.50';
                      return 'transparent';
                    };
                    const getHoverBgColor = () => {
                      if (nota.cancelada) return 'red.100';
                      if (foraDePeriodo) return 'orange.100';
                      return 'gray.50';
                    };
                    return (
                      <React.Fragment key={nota.id}>
                        <Tr
                          _hover={{ bg: getHoverBgColor() }}
                          cursor="pointer"
                          bg={getBgColor()}
                          opacity={nota.cancelada ? 0.85 : 1}
                        >
                          {/* Ações */}
                          <Td textAlign="center">
                            <HStack spacing={0} justify="center">
                              <Tooltip label="Exportar PDF" placement="top">
                                <IconButton
                                  aria-label="Exportar PDF"
                                  icon={<Icon as={FileText} boxSize={4} />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={e => {
                                    e.stopPropagation();
                                    generateNfsePdf(nota);
                                  }}
                                />
                              </Tooltip>
                              {nota.chave_nfse && (
                                <Tooltip label="Consulta pública NFS-e" placement="top">
                                  <IconButton
                                    aria-label="Consulta pública NFS-e"
                                    icon={<Icon as={ExternalLink} boxSize={4} />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="teal"
                                    as="a"
                                    href={`https://www.nfse.gov.br/consultapublica?tpc=1&chave=${nota.chave_nfse}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                  />
                                </Tooltip>
                              )}
                            </HStack>
                          </Td>
                          {/* Data Nota */}
                          <Td fontSize="xs">{formatDate(nota.data_emissao)}</Td>
                          {/* Competência */}
                          <Td fontSize="xs">{formatDate(nota.data_competencia)}</Td>
                          {/* Número NF */}
                          <Td fontWeight="medium" fontSize="sm">
                            <HStack spacing={2}>
                              <Text textDecoration={nota.cancelada ? 'line-through' : 'none'}>
                                {nota.numero_nfse || '-'}
                              </Text>
                              {nota.cancelada &&
                                (() => {
                                  const foiSubstituida =
                                    !!nota.chave_substituta &&
                                    (nota.motivo_cancelamento || '')
                                      .toLowerCase()
                                      .includes('substitu');
                                  const prefixo = foiSubstituida ? 'Substituída' : 'Cancelada';
                                  const dataFmt = nota.data_cancelamento
                                    ? moment(nota.data_cancelamento).format('DD/MM/YYYY HH:mm')
                                    : '-';
                                  return (
                                    <Tooltip label={`${prefixo} em ${dataFmt}`} placement="top">
                                      <span>
                                        <XCircle
                                          size={16}
                                          color={foiSubstituida ? '#805AD5' : '#E53E3E'}
                                          weight="fill"
                                        />
                                      </span>
                                    </Tooltip>
                                  );
                                })()}
                            </HStack>
                          </Td>
                          {/* Nome Emitente */}
                          <Td maxW="200px" isTruncated>
                            <Text fontSize="xs" fontWeight="medium" isTruncated>
                              {nota.emissor_nome || '-'}
                            </Text>
                          </Td>
                          {/* CNPJ/CPF Emitente */}
                          <Td fontSize="xs">
                            {(() => {
                              if (nota.emissor_cnpj) return formatCNPJ(nota.emissor_cnpj);
                              return nota.emissor_cpf ? formatCPF(nota.emissor_cpf) : '-';
                            })()}
                          </Td>
                          {/* Cidade Emitente */}
                          <Td fontSize="xs">{nota.local_emissao || '-'}</Td>
                          {/* Nome Tomador */}
                          <Td maxW="200px" isTruncated>
                            <Text fontSize="xs" fontWeight="medium" isTruncated>
                              {nota.tomador_nome || '-'}
                            </Text>
                          </Td>
                          {/* CNPJ/CPF Tomador */}
                          <Td fontSize="xs">
                            {(() => {
                              if (nota.tomador_cnpj) return formatCNPJ(nota.tomador_cnpj);
                              return nota.tomador_cpf ? formatCPF(nota.tomador_cpf) : '-';
                            })()}
                          </Td>
                          {/* Cidade Tomador */}
                          <Td fontSize="xs">
                            {nota.tomador_nome_municipio
                              ? `${nota.tomador_nome_municipio}${nota.tomador_uf ? ` - ${nota.tomador_uf}` : ''}`
                              : '-'}
                          </Td>
                          {/* M. Incidência */}
                          <Td textAlign="center">
                            <Tooltip
                              label={getTribIssqnTooltip(nota.trib_issqn)}
                              placement="top"
                              isDisabled={!nota.trib_issqn}
                            >
                              <Badge
                                size="sm"
                                colorScheme={getTribIssqnColor(nota.trib_issqn)}
                                fontSize="xs"
                              >
                                {getTribIssqnLabel(nota.trib_issqn, nota.local_incidencia)}
                              </Badge>
                            </Tooltip>
                          </Td>
                          {/* Tipo */}
                          <Td textAlign="center">
                            <VStack spacing={1}>
                              <Badge
                                size="sm"
                                colorScheme={nota.tipo === 'prestado' ? 'green' : 'blue'}
                              >
                                {nota.tipo === 'prestado' ? 'Prestada' : 'Tomada'}
                              </Badge>
                              {nota.cancelada &&
                                (() => {
                                  const foiSubstituida =
                                    !!nota.chave_substituta &&
                                    (nota.motivo_cancelamento || '')
                                      .toLowerCase()
                                      .includes('substitu');
                                  return (
                                    <Badge
                                      size="sm"
                                      colorScheme={foiSubstituida ? 'purple' : 'red'}
                                      variant="solid"
                                    >
                                      {foiSubstituida ? 'Substituída' : 'Cancelada'}
                                    </Badge>
                                  );
                                })()}
                              {isEmissaoForaDePeriodo(nota) && (
                                <Tooltip
                                  label={`Emitida em ${formatDate(nota.data_emissao)}, competência ${formatDate(nota.data_competencia)}`}
                                  placement="top"
                                >
                                  <Badge size="sm" colorScheme="orange" variant="solid">
                                    Fora do período
                                  </Badge>
                                </Tooltip>
                              )}
                            </VStack>
                          </Td>
                          {/* Vr. Contábil */}
                          <Td fontWeight="bold" fontSize="xs" textAlign="right">
                            {formatCurrency(nota.valor_serv_prest || 0)}
                          </Td>
                          {/* Base Cálculo */}
                          <Td fontSize="xs" textAlign="right">
                            {formatCurrency(nota.valor_bc || 0)}
                          </Td>
                          {/* Aliq ISS */}
                          <Td fontSize="xs" textAlign="center">
                            {nota.aliquota_aplic ? `${nota.aliquota_aplic}%` : '-'}
                          </Td>
                          {/* ISS Normal (apuração própria — retido fica só na coluna ISS Ret) */}
                          <Td fontSize="xs" textAlign="right">
                            {getIssProprio(nota) ? formatCurrency(getIssProprio(nota)) : '-'}
                          </Td>
                          {/* ISS Ret */}
                          <Td fontSize="xs" textAlign="right">
                            {isIssRetido(nota) ? formatCurrency(getIssApurado(nota)) : '-'}
                          </Td>
                          {/* IRRF */}
                          <Td fontSize="xs" textAlign="right">
                            {nota.v_ret_irrf || nota.irrf
                              ? formatCurrency(nota.v_ret_irrf || nota.irrf || 0)
                              : '-'}
                          </Td>
                          {/* PIS — débito de apuração própria (NT-008 §2.1.9): sempre exibido;
                              a retenção é indicada na coluna Ret. PIS/COF */}
                          <Td fontSize="xs" textAlign="right">
                            {nota.v_pis || nota.pis
                              ? formatCurrency(nota.v_pis || nota.pis || 0)
                              : '-'}
                          </Td>
                          {/* COFINS — débito de apuração própria: sempre exibido */}
                          <Td fontSize="xs" textAlign="right">
                            {nota.v_cofins || nota.cofins
                              ? formatCurrency(nota.v_cofins || nota.cofins || 0)
                              : '-'}
                          </Td>
                          {/* Ret. PIS/COFINS — indicador do leiaute traduzido pela
                              tabela oficial da NT-007 (ver getRetPisCofinsLabel) */}
                          <Td fontSize="xs" textAlign="center">
                            {getRetPisCofinsLabel(nota.tp_ret_pis_cofins)}
                          </Td>
                          {/* CSLL */}
                          <Td fontSize="xs" textAlign="right">
                            {nota.v_ret_csll || nota.csll
                              ? formatCurrency(nota.v_ret_csll || nota.csll || 0)
                              : '-'}
                          </Td>
                          {/* INSS */}
                          <Td fontSize="xs" textAlign="right">
                            {nota.v_ret_inss || nota.inss
                              ? formatCurrency(nota.v_ret_inss || nota.inss || 0)
                              : '-'}
                          </Td>
                          {/* Cód. Trib. Nacional */}
                          <Td fontSize="xs" textAlign="center">
                            {nota.servico_codigo || '-'}
                          </Td>
                          {/* Desc. Trib. Nacional */}
                          <Td maxW="200px" isTruncated>
                            <Tooltip label={nota.tributo_nacional || ''} placement="top">
                              <Text fontSize="xs" isTruncated>
                                {nota.tributo_nacional || '-'}
                              </Text>
                            </Tooltip>
                          </Td>
                          {/* Chave NFS-e */}
                          <Td fontSize="xs">
                            <Tooltip label={nota.chave_nfse || ''} placement="top">
                              <Text isTruncated maxW="150px" fontFamily="mono">
                                {nota.chave_nfse || '-'}
                              </Text>
                            </Tooltip>
                          </Td>
                          {/* IM Emitente */}
                          <Td fontSize="xs">{nota.emissor_im || nota.prestador_im || '-'}</Td>
                          {/* Discriminação */}
                          <Td maxW="250px" isTruncated>
                            <Tooltip label={nota.servico_descricao || ''} placement="top">
                              <Text fontSize="xs" isTruncated>
                                {nota.servico_descricao || '-'}
                              </Text>
                            </Tooltip>
                          </Td>
                          {/* Cód Trib. Município */}
                          <Td fontSize="xs" textAlign="center">
                            {nota.servico_codigo_trib_mun || '-'}
                          </Td>
                          {/* Município Incidência (ISSQN) */}
                          <Td fontSize="xs">{nota.local_incidencia || '-'}</Td>
                        </Tr>
                      </React.Fragment>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </TableContainer>

          {/* Paginação */}
          {nfseList.length > 0 && <Paginator pagination={pagination} />}
        </div>
      </FormBox>

      <ExportPdfModal
        isOpen={isExportPdfModalOpen}
        onClose={() => setIsExportPdfModalOpen(false)}
        onExport={handleExportTablePdf}
        isExporting={isExportingPdf}
        totalNotas={pagination?.total || nfseList.length}
      />
    </PageBox>
  );
}
